/**
 * HQ -> New Joiners: onboarding tracker for new staff.
 *
 * One row per joiner; pick a row to see its checklist and click a step to move
 * it pending -> in progress -> done. Tracker only — nothing is sent from here
 * (user, 2026-10-08). The step list and which roles skip which steps come from
 * the backend (oh_operations/models/new_joiner.py STEPS), so the page never
 * hard-codes them.
 */
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import dayjs from 'dayjs'
import React from 'react'

import {
  createNewJoiner,
  deleteNewJoiner,
  fetchNewJoiners,
  updateNewJoiner
} from 'api/newJoiners'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

const NEXT_STATUS = {
  pending: 'in_progress',
  in_progress: 'done',
  done: 'pending'
}

const STATUS_STYLE = {
  pending: { label: 'Pending', bg: '#f1f1f1', fg: '#616161' },
  in_progress: { label: 'In progress', bg: '#fff4e0', fg: '#8a5300' },
  done: { label: 'Done', bg: '#e6f4ea', fg: '#1e6b34' }
}

const EMPTY_FORM = { name: '', role: '', centre: '', joining_date: '' }

const errorDetail = (e) => e?.response?.data?.detail || constants.ERROR_MESSAGE

const formatDate = (iso) => (iso ? dayjs(iso).format('D MMM YYYY') : '')

const isThisWeek = (iso) => {
  const d = dayjs(iso)
  const monday = dayjs()
    .startOf('day')
    .subtract((dayjs().day() + 6) % 7, 'day')
  return !d.isBefore(monday) && d.isBefore(monday.add(7, 'day'))
}

const SummaryCard = ({ label, value }) => (
  <Paper variant="outlined" style={{ padding: '12px 16px' }}>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="h5">{value}</Typography>
  </Paper>
)

const JoinerDialog = ({ open, initial, roles, centres, onClose, onSave }) => {
  const [form, setForm] = React.useState(EMPTY_FORM)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    if (open) {
      setForm(initial || EMPTY_FORM)
      setError('')
    }
  }, [open, initial])

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value })
    setError('')
  }

  const submit = async () => {
    if (!form.name.trim() || !form.role || !form.centre || !form.joining_date) {
      setError('Fill in name, role, centre and joining date.')
      return
    }
    setSaving(true)
    try {
      await onSave(form)
    } catch (e) {
      setError(errorDetail(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{initial ? 'Edit joiner' : 'Add joiner'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          value={form.name}
          onChange={set('name')}
          fullWidth
          margin="dense"
          autoFocus
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Role</InputLabel>
          <Select value={form.role} onChange={set('role')}>
            {roles.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel>Centre</InputLabel>
          <Select value={form.centre} onChange={set('centre')}>
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Joining date"
          type="date"
          value={form.joining_date}
          onChange={set('joining_date')}
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
        />
        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={submit}
          color="primary"
          variant="contained"
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const StepTile = ({ index, step, onClick, busy }) => {
  const style = STATUS_STYLE[step.status] || STATUS_STYLE.pending
  return (
    <Paper
      variant="outlined"
      onClick={busy ? undefined : onClick}
      style={{
        padding: '10px 12px',
        cursor: busy ? 'progress' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 10
      }}
    >
      <Typography variant="caption" color="textSecondary" style={{ width: 18 }}>
        {index + 1}
      </Typography>
      <Box flex={1} minWidth={0}>
        <Typography variant="body2">{step.label}</Typography>
        {step.status !== 'pending' && step.at && (
          <Typography variant="caption" color="textSecondary">
            {step.by ? `${step.by} · ` : ''}
            {formatDate(step.at)}
          </Typography>
        )}
      </Box>
      <span
        style={{
          fontSize: 12,
          padding: '2px 8px',
          borderRadius: 4,
          background: style.bg,
          color: style.fg,
          whiteSpace: 'nowrap'
        }}
      >
        {style.label}
      </span>
    </Paper>
  )
}

const NewJoiners = () => {
  const [data, setData] = React.useState(null)
  const [loadError, setLoadError] = React.useState('')
  const [roleFilter, setRoleFilter] = React.useState('')
  const [centreFilter, setCentreFilter] = React.useState('')
  const [selected, setSelected] = React.useState(null)
  const [dialog, setDialog] = React.useState(null) // null | 'add' | 'edit'
  const [busyStep, setBusyStep] = React.useState('')

  const load = React.useCallback(async () => {
    try {
      setData(await fetchNewJoiners())
      setLoadError('')
    } catch (e) {
      setLoadError(errorDetail(e))
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  const joiners = React.useMemo(
    () =>
      (data?.joiners || []).filter(
        (j) =>
          (!roleFilter || j.role === roleFilter) &&
          (!centreFilter || j.centre.uuid === centreFilter)
      ),
    [data, roleFilter, centreFilter]
  )

  // The tracker opens only when a name is clicked (user, 2026-10-08) — no
  // default selection. Filtering the joiner out of view closes it.
  const current = joiners.find((j) => j.uuid === selected) || null

  // Memoized so the dialog's reset-on-open effect doesn't fire on every render
  // and wipe what's being typed.
  const editing = dialog === 'edit' ? current : null
  const dialogInitial = React.useMemo(
    () =>
      editing
        ? {
            name: editing.name,
            role: editing.role,
            centre: editing.centre.uuid,
            joining_date: editing.joining_date
          }
        : null,
    [editing]
  )

  const replaceJoiner = (row) =>
    setData((d) => ({
      ...d,
      joiners: d.joiners.map((j) => (j.uuid === row.uuid ? row : j))
    }))

  const cycleStep = async (step) => {
    setBusyStep(step.key)
    try {
      replaceJoiner(
        await updateNewJoiner(current.uuid, {
          step: step.key,
          status: NEXT_STATUS[step.status] || 'in_progress'
        })
      )
    } catch (e) {
      showToast(errorDetail(e), constants.ERROR)
    } finally {
      setBusyStep('')
    }
  }

  const save = async (form) => {
    const body = { ...form, name: form.name.trim() }
    if (dialog === 'edit') {
      replaceJoiner(await updateNewJoiner(current.uuid, body))
      showToast('Joiner updated', constants.SUCCESS)
    } else {
      const row = await createNewJoiner(body)
      setData((d) => ({ ...d, joiners: [row, ...d.joiners] }))
      showToast('Joiner added', constants.SUCCESS)
    }
    setDialog(null)
  }

  const removeCurrent = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${current.name} from New Joiners?`)) return
    try {
      await deleteNewJoiner(current.uuid)
      setData((d) => ({
        ...d,
        joiners: d.joiners.filter((j) => j.uuid !== current.uuid)
      }))
      showToast('Joiner removed', constants.SUCCESS)
    } catch (e) {
      showToast(errorDetail(e), constants.ERROR)
    }
  }

  if (loadError) {
    return (
      <Box p={3}>
        <Typography color="error">{loadError}</Typography>
      </Box>
    )
  }
  if (!data) {
    return (
      <Box p={6} textAlign="center">
        <CircularProgress />
      </Box>
    )
  }

  const inProgress = joiners.filter((j) => j.done < j.total).length
  const thisWeek = joiners.filter((j) => isThisWeek(j.joining_date)).length
  const complete = joiners.filter((j) => j.total && j.done === j.total).length

  return (
    <Box p={3}>
      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        mb={2}
        style={{ gap: 12 }}
      >
        <Typography variant="h5" style={{ flex: 1 }}>
          New Joiners
        </Typography>
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Role</InputLabel>
          <Select
            label="Role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <MenuItem value="">All roles</MenuItem>
            {data.roles.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
          <InputLabel>Centre</InputLabel>
          <Select
            label="Centre"
            value={centreFilter}
            onChange={(e) => setCentreFilter(e.target.value)}
          >
            <MenuItem value="">All centres</MenuItem>
            {data.centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          color="primary"
          variant="contained"
          onClick={() => setDialog('add')}
        >
          Add joiner
        </Button>
      </Box>

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={4}>
          <SummaryCard label="In progress" value={inProgress} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard label="Joining this week" value={thisWeek} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard label="Fully onboarded" value={complete} />
        </Grid>
      </Grid>

      <Paper variant="outlined" style={{ marginBottom: 16, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Centre</TableCell>
              <TableCell>Joins</TableCell>
              <TableCell style={{ width: 220 }}>Progress</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {joiners.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="textSecondary">
                    No joiners here yet. Use Add joiner to start one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {joiners.map((j) => (
              <TableRow key={j.uuid} selected={current?.uuid === j.uuid}>
                <TableCell>
                  <Link
                    component="button"
                    variant="body2"
                    style={{ fontWeight: 600, textAlign: 'left' }}
                    onClick={() =>
                      setSelected(current?.uuid === j.uuid ? null : j.uuid)
                    }
                  >
                    {j.name}
                  </Link>
                </TableCell>
                <TableCell>{j.role_label}</TableCell>
                <TableCell>{j.centre.name}</TableCell>
                <TableCell>{formatDate(j.joining_date)}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Box flex={1}>
                      <LinearProgress
                        variant="determinate"
                        value={
                          j.total ? Math.round((j.done / j.total) * 100) : 0
                        }
                      />
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {j.done}/{j.total}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {current && (
        <Paper variant="outlined" style={{ padding: 16 }}>
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            mb={2}
            style={{ gap: 8 }}
          >
            <Box flex={1}>
              <Typography variant="h6">{current.name}</Typography>
              <Typography variant="body2" color="textSecondary">
                {current.role_label} · {current.centre.name} · joins{' '}
                {formatDate(current.joining_date)} · {current.done} of{' '}
                {current.total} done
              </Typography>
            </Box>
            <Button size="small" onClick={() => setDialog('edit')}>
              Edit
            </Button>
            <Button size="small" onClick={removeCurrent}>
              Remove
            </Button>
            <Button size="small" onClick={() => setSelected(null)}>
              Close
            </Button>
          </Box>
          <Grid container spacing={1}>
            {current.steps.map((step, i) => (
              <Grid item xs={12} sm={6} key={step.key}>
                <StepTile
                  index={i}
                  step={step}
                  busy={busyStep === step.key}
                  onClick={() => cycleStep(step)}
                />
              </Grid>
            ))}
          </Grid>
          <Typography
            variant="caption"
            color="textSecondary"
            component="p"
            style={{ marginTop: 8 }}
          >
            Click a step to move it pending → in progress → done.
          </Typography>
        </Paper>
      )}

      <JoinerDialog
        open={Boolean(dialog)}
        initial={dialogInitial}
        roles={data.roles}
        centres={data.centres}
        onClose={() => setDialog(null)}
        onSave={save}
      />
    </Box>
  )
}

export default NewJoiners
